import { Button, Callout } from '@provenance/design';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Download } from 'lucide-react';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { publicRequest } from '../api';

/**
 * Where the "Download the certificate" link in an approval email lands.
 *
 * Until now that button pointed at `CONSOLE_URL/documents/:id` — the chamber's own console,
 * behind a login no exporter has. The email promised a download the person it was addressed
 * to could not perform, on every channel.
 *
 * The token in the URL is the whole authorisation, and it was emailed only to an address
 * the chamber holds on file. The page redirects straight to a short-lived storage link, so
 * the certificate itself is never served from this origin.
 */
export function Certificate() {
  const { token = '' } = useParams();

  const query = useQuery({
    queryKey: ['certificate', token],
    queryFn: () => publicRequest<{ url: string; filename: string }>(`/api/v1/public/certificate/${token}`),
    retry: false,
  });

  useEffect(() => {
    // Straight to the file. Someone who clicked "download" wants the document, not a page
    // with a second button on it.
    if (query.data?.url) window.location.replace(query.data.url);
  }, [query.data]);

  if (query.isError) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16">
        <Callout
          tone="stop"
          icon={<AlertTriangle className="size-4" />}
          title="This link cannot be used"
        >
          Certificate links expire six months after the certificate is issued. Ask the
          chamber that issued it to send you a new one.
        </Callout>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-16 text-center">
      <p className="[font-size:14px] text-[var(--pv-muted)]">
        {query.isLoading ? 'Fetching your certificate…' : 'Your download should begin shortly.'}
      </p>
      {query.data?.url && (
        // A fallback for a browser that blocked the redirect, and for anyone using a
        // screen reader who would otherwise be told nothing happened.
        <div className="mt-4">
          <Button
            variant="primary"
            leadingIcon={<Download className="size-3.5" />}
            onClick={() => window.location.assign(query.data.url)}
          >
            Download {query.data.filename}
          </Button>
        </div>
      )}
    </div>
  );
}
